import React, { FC, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import styled from 'styled-components'

import { CalendarDay, CalendarDayCellType } from 'src/components/molecules'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(isBetween)

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: 60px repeat(5, 48px) 60px;
  grid-template-rows: repeat(6, 28px);
  row-gap: 15px;
  justify-items: center;
  align-items: center;
`

type Props = {
  startDate?: Date
  endDate?: Date
  targetDate?: Date
  onClickDay: (input: { startDate: Date; endDate?: Date }) => void
  disabledAfterDate?: Date
  className?: string
}

export const RangeCalendarDays: FC<Props> = ({
  startDate,
  endDate,
  targetDate,
  onClickDay,
  disabledAfterDate,
  className,
}) => {
  const [hoverDate, setHoverDate] = useState<dayjs.Dayjs | undefined>(undefined)

  const calendarDays = useMemo(() => {
    const date = targetDate ?? startDate ?? new Date()
    const year = date.getFullYear()
    const month = date.getMonth()

    // 該当月の日数分 dayjs のオブジェクトを作成する
    const lastDayOfMonth = dayjs(date).endOf('month').date()
    return Array.from({ length: lastDayOfMonth }).map((_, index) => {
      return dayjs(`${year}-${month + 1}-${index + 1}`, 'YYYY-MM-DD')
    })
  }, [startDate, targetDate])

  const handleOnClickDay = (day: dayjs.Dayjs) => {
    if (!day.isSameOrBefore(dayjs())) {
      return
    }

    // どちらも選択されている状態、もしくはまだ日付が選択されていない状態で、日付をクリックした場合は最初の日から再度選択する
    if ((startDate && endDate) || !startDate) {
      onClickDay({ startDate: day.toDate() })
      setHoverDate(undefined)
      return
    }

    let first = dayjs(startDate)
    let second = day

    if (first.isAfter(second)) {
      const temp = second
      second = first
      first = temp
    }

    onClickDay({ startDate: first.toDate(), endDate: second.toDate() })
  }

  const loadCellType = (day: dayjs.Dayjs): CalendarDayCellType => {
    const firstDate = startDate
    const secondDate = endDate || hoverDate

    if (!firstDate || !secondDate) {
      return 'none'
    }

    let first = dayjs(firstDate)
    let second = dayjs(secondDate)

    if (first.isAfter(second)) {
      const temp = second
      second = first
      first = temp
    }

    if (day.isSame(first, 'day')) {
      return 'right'
    }
    if (day.isBetween(first, second, 'day')) {
      return 'full'
    }
    if (day.isSame(second, 'day')) {
      return 'left'
    }
    return 'none'
  }

  return (
    <div className={className}>
      <CalendarGrid>
        {calendarDays.map(day => (
          <CalendarDay
            key={day.toString()}
            day={day}
            cellType={loadCellType(day)}
            isSelected={
              (!!startDate && day.format('YYYY-MM-DD') === dayjs(startDate).format('YYYY-MM-DD')) ||
              (!!endDate && day.format('YYYY-MM-DD') === dayjs(endDate).format('YYYY-MM-DD'))
            }
            onClick={handleOnClickDay}
            disabledAfterDate={disabledAfterDate ? dayjs(disabledAfterDate) : undefined}
            onHover={setHoverDate}
          />
        ))}
      </CalendarGrid>
    </div>
  )
}
